// Eskom PARCC — badge primitives.
//
// Wraps the `.badge` pill plus the compliance-colour `.c-*` classes from
// components.css. Compliance levels are multi-word strings (e.g.
// "Non-Compliance: Possible IE"). A class attribute splits on whitespace, so a
// verbatim `c-${level}` suffix would land on the element as SEVERAL tokens
// (`c-Non-Conformance:`, `Possible`, `IE`) and the colour selector would never
// match — every multi-word level rendered colourless. We therefore slugify the
// level into a single whitespace-free token via the pure `complianceClass`
// helper; the `.c-*` selectors in components.css use the matching slug form.
// A neutral fallback covers empty/unknown levels so nothing renders unstyled.
import type { ReactNode } from 'react';

// Neutral pill used when there is no level to colour by (empty/nullish). It is a
// real, defined `.c-*` rule, so the badge still reads as on-brand rather than
// rendering an unstyled span. Slug form, matching `.c-Not-Reviewed` in the CSS.
export const NEUTRAL_COMPLIANCE_CLASS = 'c-Not-Reviewed';

/**
 * Map a compliance-level string to its `.c-*` colour class.
 *
 * The class is `c-` followed by a slug of the level: every run of
 * non-alphanumeric characters (spaces, colons, ampersands, hyphens) collapses to
 * a single `-`, with leading/trailing hyphens stripped. Case is preserved. This
 * yields a single class token that matches the slugified `.c-*` selectors in
 * components.css — unlike a verbatim suffix, which the DOM would split on spaces.
 *
 * Empty, whitespace-only or nullish levels fall back to {@link NEUTRAL_COMPLIANCE_CLASS}.
 * Levels with no dedicated `.c-*` rule (e.g. "Deleted") still get a `c-…` class
 * and simply inherit the base `.badge` styling.
 */
export function complianceClass(level: string | null | undefined): string {
  if (typeof level !== 'string') return NEUTRAL_COMPLIANCE_CLASS;
  const slug = level.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
  if (slug === '') return NEUTRAL_COMPLIANCE_CLASS;
  return `c-${slug}`;
}

export function Badge({
  children,
  className,
  title,
}: {
  children: ReactNode;
  /** Extra class(es) to compose onto `.badge` (e.g. a `c-*` colour class). */
  className?: string;
  title?: string;
}): React.JSX.Element {
  return (
    <span className={className ? `badge ${className}` : 'badge'} title={title}>
      {children}
    </span>
  );
}

/**
 * A compliance-level badge: shows the level label, coloured by its `.c-*` class.
 * Empty/unknown levels render the level text (or a dash) on a neutral pill.
 */
export function ComplianceBadge({
  level,
}: {
  level: string | null | undefined;
}): React.JSX.Element {
  const label = typeof level === 'string' && level.trim() !== '' ? level : '—';
  return (
    <Badge className={complianceClass(level)} title={label}>
      {label}
    </Badge>
  );
}

/**
 * A CES-status badge. CES statuses ("Compliant", "Non-Compliant - PFMA", …) are a
 * distinct vocabulary from compliance levels and mostly have no dedicated `.c-*`
 * rule, so they share the same slugged `complianceClass` scheme — known overlaps
 * like "Compliant" pick up the matching colour, the rest read as neutral pills.
 */
export function CesStatusBadge({
  status,
}: {
  status: string | null | undefined;
}): React.JSX.Element {
  const label = typeof status === 'string' && status.trim() !== '' ? status : '—';
  return (
    <Badge className={complianceClass(status)} title={label}>
      {label}
    </Badge>
  );
}
